import * as Sentry from '@sentry/nextjs';

/**
 * Falha ao nível da rede: o `fetch` nunca chegou a receber resposta.
 *
 * Porque é que isto é uma classe e não um filtro por mensagem: o `fetch` rejeita
 * com um `TypeError` cuja mensagem muda de motor para motor —
 *
 *   - WebKit  → `Load failed`
 *     (Safari macOS/iOS e *todos* os browsers em iOS, Chrome incluído, porque em
 *     iOS são obrigados a usar WebKit)
 *   - Blink   → `Failed to fetch`
 *     (Chrome/Edge desktop e Android)
 *   - Gecko   → `NetworkError when attempting to fetch resource.`
 *     (Firefox desktop e Android)
 *
 * Comparar strings obrigava a manter uma lista por browser e falhava em qualquer
 * motor novo. Aqui classificamos pela POSIÇÃO da rejeição: se rebentou na
 * chamada ao `fetch`, é rede — independentemente do browser e da mensagem.
 *
 * `error instanceof TypeError` sozinho não serve como sinal: o nosso próprio
 * código também atira `TypeError` (ex.: `x?.map is not a function` sobre um
 * payload inesperado) e esses queremos continuar a ver no Sentry.
 */
export class NetworkUnavailableError extends Error {
  readonly context!: string;

  readonly originalError!: unknown;

  constructor(context: string, originalError: unknown) {
    super(
      `Request never completed (${context}): ${
        originalError instanceof Error ? originalError.message : 'unknown cause'
      }`
    );

    // Não-enumeráveis de propósito. Há handlers de erro que fazem
    // `Object.keys(error).map(...)` para listar ao utilizador os campos que a
    // API devolveu — o `onError` do `language-selector` é um deles. Uma falha de
    // rede não tem campos para mostrar, e com estas três propriedades
    // enumeráveis o utilizador via `context: PUT /…` e `originalError: …` no
    // ecrã. Com `defineProperties`, `Object.keys()` volta a devolver [].
    Object.defineProperties(this, {
      name: {
        value: 'NetworkUnavailableError',
        enumerable: false,
        writable: true,
        configurable: true,
      },
      context: { value: context, enumerable: false, configurable: true },
      originalError: {
        value: originalError,
        enumerable: false,
        configurable: true,
      },
    });
  }
}

/**
 * Marca as rejeições de rede de um pedido.
 *
 * IMPORTANTE: passa-lhe APENAS a promise do `fetch`, nunca uma cadeia que já
 * inclua `.json()` ou qualquer leitura do payload — o `fetch` só rejeita por
 * rede/CORS/abort, mas um `.json()` sobre um 500 em HTML rejeita com
 * `SyntaxError` e esse é um erro real que tem de continuar a chegar ao Sentry.
 *
 *   const response = await asNetworkAware('PUT /x', fechFromBPartsApi('/x', …));
 *   return response.json(); // ← fora do wrapper, de propósito
 *
 * Não usar com `enableCustomErrorWrapper`, que já rejeita com `CustomError`.
 */
export const asNetworkAware = <T>(
  context: string,
  request: Promise<T>
): Promise<T> =>
  request.catch((originalError) => {
    throw new NetworkUnavailableError(context, originalError);
  });

/**
 * Trata a falha de um pedido cujo resultado ninguém espera (fire-and-forget).
 *
 * Falhas de rede não são reportadas: não são acionáveis (utilizador em mobile a
 * perder rede, ecrã a bloquear, tab fechada a meio) e o Sentry já registou o
 * pedido falhado como breadcrumb, portanto continuam visíveis no rasto de
 * qualquer erro real que venha depois. Tudo o que não seja rede é reportado com
 * contexto, para não perdermos sinal de 5xx nem de payloads inesperados.
 */
export const reportRequestFailure = (context: string, error: unknown): void => {
  if (error instanceof NetworkUnavailableError) {
    return;
  }

  Sentry.captureException(error, { tags: { failed_request: context } });
};

/** Versão curried de `reportRequestFailure`, para `.catch()` e `onError`. */
export const handleRequestFailure =
  (context: string) =>
  (error: unknown): void =>
    reportRequestFailure(context, error);
